import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye, EyeOff, KeyRound, Lock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * Self-contained change-password dialog. Used in two flows:
 *
 *  1. **Forced rotation** (`forced` prop true) — opens automatically when
 *     `user.must_change_password === true`. The Cancel button is hidden;
 *     the dialog is non-dismissable until the password is rotated.
 *
 *  2. **Voluntary** — triggered from the "Change password" item in the
 *     header user-menu. Cancel re-enabled.
 *
 * Both flows POST to `/api/auth/change-password` then `auth.refresh()` to
 * update the cached `user.must_change_password` flag.
 */
export function ChangePasswordDialog({ open, onClose, forced = false }) {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset fields whenever the dialog re-opens.
  useEffect(() => {
    if (open) {
      setCurrent("");
      setNext("");
      setConfirm("");
      setShowCurrent(false);
      setShowNew(false);
    }
  }, [open]);

  const validation = (() => {
    if (next.length === 0) return null; // don't show errors before they type
    if (next.length < 8) return "New password must be at least 8 characters.";
    if (next === current) return "New password must be different from your current one.";
    if (confirm.length > 0 && confirm !== next) return "Passwords don't match.";
    return null;
  })();

  const canSubmit =
    !busy &&
    current.length > 0 &&
    next.length >= 8 &&
    next !== current &&
    confirm === next;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. You're all set.");
      await refresh();
      if (onClose) onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update password.");
    } finally {
      setBusy(false);
    }
  };

  // Forced rotation locks the dialog open: ignore close attempts until
  // `must_change_password` flips to false (which `refresh()` after success
  // will surface to the parent).
  const handleOpenChange = (next) => {
    if (!next && forced) return;
    if (!next && onClose) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`max-w-md ${forced ? "[&>button]:hidden" : ""}`}
        data-testid="change-password-dialog"
        onPointerDownOutside={forced ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={forced ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {forced ? <ShieldAlert className="h-5 w-5 text-amber-600" /> : <KeyRound className="h-5 w-5 text-[#1A2744]" />}
            {forced ? "Set a new password" : "Change password"}
          </DialogTitle>
          {forced && (
            <DialogDescription data-testid="change-password-forced-banner">
              Welcome, {user?.name?.split(" ")[0] || "there"}. For security, please choose a new password before you continue.
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <PasswordField
            id="current_password"
            label="Current password"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            onToggleShow={() => setShowCurrent((v) => !v)}
            autoComplete="current-password"
            testid="cp-current"
          />
          <PasswordField
            id="new_password"
            label="New password"
            value={next}
            onChange={setNext}
            show={showNew}
            onToggleShow={() => setShowNew((v) => !v)}
            autoComplete="new-password"
            testid="cp-new"
          />
          <PasswordField
            id="confirm_password"
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            show={showNew} /* mirrors the new-password toggle */
            onToggleShow={() => setShowNew((v) => !v)}
            autoComplete="new-password"
            testid="cp-confirm"
          />

          {validation && (
            <div className="text-xs text-red-600" data-testid="cp-validation">
              {validation}
            </div>
          )}
          <div className="text-[11px] text-gray-500">
            Use at least 8 characters. Mix in numbers and symbols for extra safety.
          </div>

          <DialogFooter className="pt-2">
            {!forced && (
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={busy}
                data-testid="cp-cancel"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-[#1A2744] hover:bg-[#263A63]"
              data-testid="cp-submit"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A reusable password row with the eye toggle baked in. Internal to this
 *  component; not exported because the rest of the app only needs the
 *  whole dialog. */
function PasswordField({ id, label, value, onChange, show, onToggleShow, autoComplete, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[#1A2744]">{label}</Label>
      <div className="relative mt-1.5">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required
          className="pl-10 pr-11 h-11"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={onToggleShow}
          aria-label={show ? "Hide password" : "Show password"}
          aria-pressed={show}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-[#1A2744] hover:bg-gray-100 transition-colors"
          data-testid={`${testid}-toggle`}
          tabIndex={0}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
